import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { isSweden } from '@app/core/entity/locale';
import { FirmSelectors } from '@app/core/state/firm/firm.selectors';
import { toFinancialYearsTransformer } from '@app/core/state/transformers/transformers';
import { CLIENT_TYPE_LIMITED_COMPANY } from '@app/shared/misc/constants';
import { openTranslatableDialogWithConfig } from '@app/shared/misc/openDialog';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Client } from 'src/app/core/entity/client';
import { FinancialYear } from 'src/app/core/entity/financialyear';
import { getDateValidator } from 'src/app/core/entity/validation';
import { ValidationStatus } from 'src/app/core/entity/validationStatus';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { FinancialYearService } from 'src/app/core/services/financialyear.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';
import { areMonthsBetweenFinancialYearsOk, isFirstDateAfterSecondDate } from 'src/app/shared/misc/dates';
import { TodayDate } from 'src/app/shared/misc/today.date';
import { TableColumn } from '../../../../table/interfaces/table-column';
import { TableHeader } from '../../../../table/interfaces/table-header';
import { EditFinancialYearDialogComponent } from './edit-financialyear-dialog.component';
import { toTitleMessage, toTextMessage } from 'src/app/core/entity/validationStrings';

@Component({
  selector: 'app-client-financialyears',
  templateUrl: './client.financialyears.component.html',
  providers: [getTranslationProvider('ClientFinancialYearsComponent'), FinancialYearService, DialogService],
})
export class ClientFinancialYearsComponent extends TranslatableComponent implements OnInit {
  @Output() update = new EventEmitter<FinancialYear[]>();

  @Input()
  set client(client: Client) {
    this.clientFinancialYears = [];
    this.reset();
    if (client) {
      this.internalClient = client;
      if (client.id !== -1) {
        this.initComponent();
      }
    }
  }
  get client() {
    return this.internalClient;
  }

  @Input()
  set defaultFinancialYear(value: FinancialYear) {
    this.internalDefaultFinancialYear = value;
    this.setDateDefaults();
  }

  public financialYearModel: FinancialYear = new FinancialYear();
  public clientFinancialYears: FinancialYear[];
  public selectedFinancialYear: FinancialYear;
  public showAddFinancialYear = false;
  public isSwedishFirm$: Observable<{ value: boolean }>;
  public columns$: Observable<TableColumn[]>;
  public columnHeaders$: Observable<TableHeader[]>;

  private columns: TableColumn[] = [
    { field: 'span.formattedStart', header: 'startDate' },
    { field: 'span.formattedEnd', header: 'endDate' },
    { field: 'largeCompany', header: 'largeCompany', hiddenForCountries: ['NL'] },
    { field: 'euCommerce', header: 'euCommerce', hiddenForCountries: ['NL'] },
    { field: 'electronicVat', header: 'electronicVat', hiddenForCountries: ['NL'] },
    { field: 'electronicCompilation', header: 'electronicCompilation', hiddenForCountries: ['NL'] },
    { field: 'firmTaxRespite', header: 'firmTaxRespite', hiddenForCountries: ['NL'] },
    { field: 'electronicTax', header: 'electronicTax', hiddenForCountries: ['NL'] },
    { field: 'action', header: '', width: '40px', cellStyle: 'padding-right:0', sortable: false },
  ];

  private sweHeaderConfig: TableHeader[] = [
    {
      columns: [
        { field: '', header: 'financialYear', colspan: 2, class: 'text-center white-space-normal' },
        { field: '', header: 'employerAndVatDeclaration', class: 'text-center white-space-normal' },
        { field: '', header: 'vatDeclaration', colspan: 2, class: 'text-center white-space-normal' },
        { field: '', header: 'periodicCompilation', class: 'text-center white-space-normal' },
        { field: '', header: 'incomeTaxReturn', colspan: 2, class: 'text-center white-space-normal' },
        { field: '', header: '', rowspan: 2, width: '30px' },
      ],
    },
    {
      columns: [
        { field: '', header: 'shortFrom', class: 'text-center' },
        { field: '', header: 'shortTo', class: 'text-center' },
        { field: '', header: 'largeCompanyTitle2', class: 'text-center white-space-normal' },
        { field: '', header: 'euCommerceTitle2', class: 'text-center white-space-normal' },
        { field: '', header: 'leavingElectronically', class: 'text-center white-space-normal' },
        { field: '', header: 'leavingElectronically', class: 'text-center white-space-normal' },
        { field: '', header: 'agencyDeferment', class: 'text-center white-space-normal' },
        { field: '', header: 'submittedElectronically', class: 'text-center white-space-normal' },
      ],
    },
  ];

  private otherHeaderConfig: TableHeader[] = [
    {
      columns: [
        { field: '', header: 'financialYear', colspan: 2, class: 'text-center white-space-normal' },
        { field: '', header: '', width: '30px' },
      ],
    },
    {
      columns: [
        { field: '', header: 'shortFrom', class: 'text-center' },
        { field: '', header: 'shortTo', class: 'text-center' },
        { field: '', header: '', width: '30px' },
      ],
    },
  ];

  private internalClient: Client;
  private internalDefaultFinancialYear: FinancialYear;

  constructor(
    private financialYearService: FinancialYearService,
    private dialogService: DialogService,
  ) {
    super();
    this.isSwedishFirm$ = this.store
      .select(FirmSelectors.selectCountryCode)
      .pipe(map((countryCode) => ({ value: isSweden(countryCode) })));

    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns));

    this.columnHeaders$ = this.isSwedishFirm$.pipe(
      map((isSwedishFirm) => (isSwedishFirm.value ? this.sweHeaderConfig : this.otherHeaderConfig)),
      switchMap((headerConfig) =>
        this.translationsLoaded$.pipe(
          filter(Boolean),
          map((translations) =>
            headerConfig.map((header) => ({
              ...header,
              columns: translateColumnHeaders(header.columns)(translations),
            })),
          ),
        ),
      ),
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initComponent();
  }

  private initComponent() {
    if (this.internalClient && this.internalClient.id !== -1) {
      this.loadFinancialYears().subscribe();
    }
  }

  private loadFinancialYears(): Observable<FinancialYear[]> {
    this.financialYearModel = new FinancialYear();
    return this.financialYearService
      .getFinancialYearsForClientId(this.internalClient.id)
      .pipe(tap((fys) => this.applyFinancialYears(fys)));
  }

  private applyFinancialYears(fys: FinancialYear[]) {
    const todayDate = new TodayDate(this.getActiveLanguage());
    const toLocalDateFormat = (date: string) => (date ? todayDate.formatDateByLocale(new Date(date)) : '');

    this.clientFinancialYears = fys.map((fy) => {
      return {
        ...fy,
        span: {
          ...fy.span,
          formattedStart: toLocalDateFormat(fy.span.start),
          formattedEnd: toLocalDateFormat(fy.span.end),
        },
      };
    });
    this.setDateDefaults();
  }

  // eslint-disable-next-line max-lines-per-function
  saveFinancialYear() {
    this.financialYearModel.clientId = this.internalClient.id;

    if (!this.validateDate(this.financialYearModel.span.start, this.translate('startDate'))) {
      return;
    }

    if (!this.validateDate(this.financialYearModel.span.end, this.translate('endDate'))) {
      return;
    }

    const isStartAfterEnd = isFirstDateAfterSecondDate(
      this.financialYearModel.span.start,
      this.financialYearModel.span.end,
    );
    if (isStartAfterEnd) {
      this.store.dispatch(
        ToastActions.showErrorMessage({
          summary: this.translate('startDateAfterEndDate'),
          detail: this.translate('startDateMustBeBeforeEndDate'),
        }),
      );
      return;
    }

    const diff = areMonthsBetweenFinancialYearsOk(this.financialYearModel.span.start, this.financialYearModel.span.end);
    if (!diff) {
      this.store.dispatch(
        ToastActions.showErrorMessage({
          summary: this.translate('invalidDateInterval'),
          detail: this.translate('dateIntervalExceedsXMonths'),
        }),
      );
    }
    this.financialYearService
      .saveFinancialYear(this.financialYearModel)
      .pipe(switchMap(() => this.loadFinancialYears()))
      .subscribe(() => {
        this.showAddFinancialYear = false;
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: this.translate('saved'),
            detail: this.translate('financialYearIsSaved'),
          }),
        );
        this.update.emit(this.clientFinancialYears);
      });
  }

  deleteFinancialYear(id: number) {
    this.financialYearService
      .deleteFinancialYearWithId(id)
      .pipe(switchMap(() => this.loadFinancialYears()))
      .subscribe(() => {
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: this.translate('saved'),
            detail: this.translate('financialYearIsDeleted'),
          }),
        );
        this.update.emit(this.clientFinancialYears);
      });
  }

  onRowSelect(event: { column: TableColumn; data: FinancialYear }) {
    if (event.column.field === 'action') {
      return;
    }

    this.financialYearModel = JSON.parse(JSON.stringify(event.data));

    openTranslatableDialogWithConfig(this.dialogService, EditFinancialYearDialogComponent, {
      data: event.data,
      header: `${event.data.span.start} - ${event.data.span.end}`,
      width: '650px',
    })
      .pipe(filter((res) => !!res))
      .subscribe((res) => {
        this.financialYearModel = toFinancialYearsTransformer.transform(res);
        this.saveFinancialYear();
      });
  }

  reset() {
    this.selectedFinancialYear = null;
    this.financialYearModel = new FinancialYear();
    this.setDateDefaults();
  }

  setDateDefaults() {
    if (this.clientFinancialYears.length > 0) {
      const fy = this.clientFinancialYears[0];
      this.financialYearModel.span = {
        start: new TodayDate().getFirstDayOfNextFinancialYearAsString(fy.span.end),
        end: new TodayDate().getLastDayOfNextFinancialYearAsString(fy.span.end),
      };
      this.financialYearModel.largeCompany = fy.largeCompany;
      this.financialYearModel.euCommerce = fy.euCommerce;
      this.financialYearModel.electronicVat = fy.electronicVat;
      this.financialYearModel.electronicCompilation = fy.electronicCompilation;
      this.financialYearModel.electronicTax = fy.electronicTax;
      this.financialYearModel.firmVatRespite = fy.firmVatRespite;
      this.financialYearModel.firmTaxRespite = fy.firmTaxRespite;
    } else {
      if (this.internalDefaultFinancialYear) {
        this.financialYearModel.span = this.internalDefaultFinancialYear.span;
      } else {
        this.financialYearModel.span = {
          start: new TodayDate().getFirstDayOfCurrentYearAsString(),
          end: new TodayDate().getLastDayOfCurrentYearAsString(),
        };
      }
    }
  }

  checkIfTaxRespiteIsPossible(checked: boolean) {
    if (
      checked &&
      (this.internalClient.type.name === CLIENT_TYPE_LIMITED_COMPANY ||
        this.internalClient.type.name === 'PARTNERSHIP' ||
        this.internalClient.type.name === 'NON_PROFIT_ASSOCIATION')
    ) {
      this.store.dispatch(
        ToastActions.showWarnMessage({
          summary: this.translate('notAllowed'),
          detail: this.translate('legalEntitiesMayNotApplyForAgencyDeferment'),
        }),
      );
      setTimeout(() => {
        this.financialYearModel.firmTaxRespite = false;
      }, 500);
    }
  }

  private validateDate(value: string, label: string): boolean {
    const { validate, min, max } = getDateValidator();
    const { status, title, text } = validate(value, { min, max, label });

    if (status !== ValidationStatus.Ok) {
      const scope = 'validation';
      const summary = toTitleMessage(this.translateWithOtherScope(scope, title.key), title);
      const detail = toTextMessage(this.translateWithOtherScope(scope, text?.key), text);

      this.store.dispatch(ToastActions.showWarnMessage({ summary, detail }));
      return false;
    }

    return true;
  }

  toggleAddFinancialYear() {
    this.showAddFinancialYear = !this.showAddFinancialYear;
  }
}
